/**
 * CookUp arpeggiator: pattern logic and a lookahead step scheduler.
 *
 * Steps are scheduled ahead on the audio clock (like the timeline), not fired
 * from a main-thread timer, so every step starts sample-accurately in the
 * Core engine and on time in the Web Audio engines, whatever the UI is doing.
 * While the transport runs, steps sit on the song's grid; otherwise the first
 * key starts the grid.
 *
 * Everything here is pure and deterministic ("random" included), so it is
 * tested without audio.
 */

export const ARP_MODES = ['up', 'down', 'up-down', 'down-up', 'played', 'random', 'chord'];
export const ARP_MODE_LABELS = {
  up: 'Up', down: 'Down', 'up-down': 'Up / Down', 'down-up': 'Down / Up',
  played: 'As played', random: 'Random', chord: 'Chord'
};
export const ARP_MAX_STEPS = 32;
const LATE_TOLERANCE = 0.035;
export const ARP_SYNC_MODES = ['transport', 'key'];

export const defaultArpStep = () => ({ on: true, velocity: 1, octave: 0, tie: false });

const clampNumber = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

/** Fill in the pattern fields a patch may not have yet. */
export function normalizeArpPattern(candidate = {}) {
  const pattern = Array.isArray(candidate.pattern) ? candidate.pattern : [];
  return {
    mode: ARP_MODES.includes(candidate.mode) ? candidate.mode : 'up',
    swing: clampNumber(candidate.swing, 0, 1, 0),
    latch: candidate.latch === true,
    sync: ARP_SYNC_MODES.includes(candidate.sync) ? candidate.sync : 'transport',
    steps: Math.round(clampNumber(candidate.steps, 1, ARP_MAX_STEPS, 16)),
    pattern: Array.from({ length: ARP_MAX_STEPS }, (_, index) => {
      const step = pattern[index] || {};
      return {
        on: step.on !== false,
        velocity: clampNumber(step.velocity, 0, 1, 1),
        octave: Math.round(clampNumber(step.octave, -2, 2, 0)),
        tie: step.tie === true
      };
    })
  };
}

/** Deterministic 0..1 value for step `k` (no reshuffling, no Math.random). */
export function stepRandom(k) {
  let x = (Math.imul(k + 1, 0x9e3779b1) ^ 0x85ebca6b) >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d) >>> 0;
  x = (x ^ (x >>> 15)) >>> 0;
  return x / 4294967296;
}

/**
 * The note cycle for the held notes: pitches (or, in chord mode, groups of
 * pitches) that successive played steps walk through.
 * `held` is [{ note, velocity, order }] where `order` is press order.
 */
export function arpCycle(held, { mode = 'up', octaves = 1 } = {}) {
  if (!held.length) return [];
  const span = Math.max(1, Math.min(4, Math.round(octaves) || 1));
  const sorted = [...held].sort((a, b) => a.note - b.note);
  const byOctave = (list) => {
    const out = [];
    for (let octave = 0; octave < span; octave += 1) {
      list.forEach((entry) => out.push({ ...entry, note: entry.note + octave * 12 }));
    }
    return out;
  };
  if (mode === 'chord') {
    return Array.from({ length: span }, (_, octave) => sorted.map((entry) => ({ ...entry, note: entry.note + octave * 12 })));
  }
  const up = byOctave(sorted);
  if (mode === 'down') return [...up].reverse();
  if (mode === 'played') return byOctave([...held].sort((a, b) => a.order - b.order));
  if (mode === 'up-down' || mode === 'down-up') {
    // Turnarounds do not repeat the top or bottom note.
    const there = mode === 'up-down' ? up : [...up].reverse();
    const back = there.slice(1, -1).reverse();
    return there.length > 1 ? [...there, ...back] : there;
  }
  return up; // 'up' and 'random' (random picks from the ascending set)
}

/** Seconds from the start of step k's grid slot to its (swung) onset. */
export const swingOffset = (k, stepSeconds, swing) => (k % 2 === 1 ? swing * stepSeconds * 0.5 : 0);

/**
 * Lookahead scheduler. Call `schedule` every ~25 ms with the window to fill;
 * it returns note events with audio-clock times and remembers where it is.
 */
export class ArpScheduler {
  constructor() {
    this.reset();
  }

  reset() {
    this.nextStep = null; // global step index k of the next slot to schedule
    this.cursor = 0; // position in the note cycle, advanced by played steps
    this.origin = 0; // audio time of step 0 in key-sync mode
    this.voice = 0;
    this.timing = null;
    this.stepSeconds = null;
  }

  get running() {
    return this.nextStep !== null;
  }

  /**
   * @param now        audio time
   * @param horizon    schedule steps whose onset is before this audio time
   * @param held       held notes [{ note, velocity, order }]
   * @param arp        normalised arp settings (rate in beats, gate, octaves, pattern…)
   * @param bps        beats per second
   * @param beatAtNow  transport beat at `now`, or null when stopped / key-synced
   * @param lead       minimum lead before `now` for a new onset (seconds)
   */
  schedule({ now, horizon, held, arp, bps, beatAtNow = null, lead = 0.005 }) {
    const events = [];
    if (!held.length) {
      this.reset();
      return events;
    }
    const stepBeats = Math.max(1 / 64, Number(arp.rate) || 0.25);
    const stepSeconds = stepBeats / Math.max(0.001, bps);
    const transport = arp.sync !== 'key' && Number.isFinite(beatAtNow);
    const slotTime = (k) => (transport
      ? now + ((k * stepBeats) - beatAtNow) / bps
      : this.origin + k * stepSeconds);

    // Switching between transport and free timing restarts the grid; a tempo
    // change in free timing keeps the next step where it was due.
    const timing = transport ? 'transport' : 'key';
    if (this.nextStep !== null && timing !== this.timing) this.nextStep = null;
    if (this.nextStep !== null && !transport && this.stepSeconds && this.stepSeconds !== stepSeconds) {
      const due = this.origin + this.nextStep * this.stepSeconds;
      this.origin = due - this.nextStep * stepSeconds;
    }
    this.timing = timing;
    this.stepSeconds = stepSeconds;

    if (this.nextStep === null) {
      if (transport) {
        // Start on the next grid step, or on this one if it only just passed
        // (a player a hair late should still land on the beat, not wait a step).
        const position = beatAtNow / stepBeats;
        const current = Math.floor(position + 1e-9);
        const late = (position - current) * stepSeconds;
        this.nextStep = late < LATE_TOLERANCE ? current : current + 1;
      } else {
        this.origin = now + lead;
        this.nextStep = 0;
      }
      this.cursor = 0;
    }

    const steps = Math.max(1, Math.min(ARP_MAX_STEPS, arp.steps || 16));
    const pattern = arp.pattern || [];
    const cycle = arpCycle(held, arp);
    const gate = clampNumber(arp.gate, 0.05, 1, 0.72);
    const swing = clampNumber(arp.swing, 0, 1, 0);

    for (let guard = 0; guard < 256; guard += 1) {
      const k = this.nextStep;
      const onset = slotTime(k) + swingOffset(k, stepSeconds, swing);
      if (onset >= horizon) break;
      this.nextStep = k + 1;
      const step = pattern[((k % steps) + steps) % steps] || defaultArpStep();
      // Rests and tied steps play nothing new; ties lengthen the note before.
      // A step at most ~35 ms overdue (a key pressed just after the beat, a
      // stalled tick) still plays, at once; anything older is dropped.
      if (!step.on || step.tie || onset < now - LATE_TOLERANCE) continue;

      // How many following steps tie onto this one.
      let tied = 0;
      while (tied < steps - 1) {
        const next = pattern[(((k + tied + 1) % steps) + steps) % steps];
        if (!next?.on || !next.tie) break;
        tied += 1;
      }
      const lastSlot = k + tied;
      const endSlotStart = slotTime(lastSlot) + swingOffset(lastSlot, stepSeconds, swing);
      const end = Math.max(onset + 0.01, endSlotStart + gate * stepSeconds);

      const entry = arp.mode === 'random'
        ? cycle[Math.floor(stepRandom(k) * cycle.length)]
        : cycle[this.cursor % cycle.length];
      this.cursor += 1;
      const group = Array.isArray(entry) ? entry : [entry];
      group.forEach((noteEntry) => {
        const note = Math.max(0, Math.min(127, noteEntry.note + (step.octave || 0) * 12));
        this.voice += 1;
        events.push({
          time: Math.max(onset, now + lead),
          end,
          note,
          velocity: Math.max(0.01, Math.min(1, noteEntry.velocity * step.velocity)),
          id: `arp:${this.voice}:${note}`,
          step: k,
          beat: transport ? beatAtNow + (onset - now) * bps : null,
          durationBeats: (end - onset) * bps
        });
      });
    }
    return events;
  }
}
